/**
 * useCompanies Hook
 * Custom hook para gestionar companies con API real
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { companiesService, type Company } from '@/services/api/companiesService';
import { useAuth } from '@/shared/contexts/AuthContext';

/**
 * Hook para obtener todas las companies
 */
export function useCompanies(params?: Record<string, any>) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['companies', params],
    queryFn: () => companiesService.getCompanies(params),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook para obtener companies con paginación server-side
 */
export function useCompaniesPaginated(
  params?: Record<string, any>,
  page: number = 1,
  pageSize: number = 50
) {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['companies-paginated', params, page, pageSize],
    queryFn: () => companiesService.getCompanies({
      ...params,
      page,
      page_size: pageSize,
    }),
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
    placeholderData: (previousData) => previousData,
  });
}

/**
 * Hook para obtener una company específica
 */
export function useCompany(id: string | undefined) {
  const { isAuthenticated } = useAuth();
  
  return useQuery({
    queryKey: ['companies', id],
    queryFn: () => companiesService.getCompanyById(id!),
    enabled: isAuthenticated && !!id,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook para obtener estadísticas de una company
 */
export function useCompanyStats(id: string | undefined) {
  const { isAuthenticated } = useAuth();
  
  return useQuery({
    queryKey: ['companies', id, 'stats'],
    queryFn: () => companiesService.getCompanyStats(id!),
    enabled: isAuthenticated && !!id,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Hook para crear company
 */
export function useCreateCompany() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: Partial<Company>) => companiesService.createCompany(data),
    onSuccess: (newCompany) => {
      // Invalidar todas las queries de companies
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      // Invalidar market específico si la compañía tiene market asignado
      if (newCompany.market) {
        queryClient.invalidateQueries({ queryKey: ['companies', { market: newCompany.market }] });
      }
    },
  });
}

/**
 * Hook para actualizar company
 */
export function useUpdateCompany() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Company> }) =>
      companiesService.updateCompany(id, data),
    onSuccess: async (updatedCompany, variables) => {
      
      // 1. Actualizar directamente el cache
      queryClient.setQueryData(['companies', variables.id], updatedCompany);
      
      // 2. Invalidar y forzar refetch
      await queryClient.invalidateQueries({ 
        queryKey: ['companies', variables.id],
        refetchType: 'active',
      });
      
      await queryClient.invalidateQueries({ 
        queryKey: ['companies'],
        exact: false,
      });
      
      // 3. Refetch explícito
      await queryClient.refetchQueries({ 
        queryKey: ['companies', variables.id],
        type: 'active',
      });
      
    },
    onError: (error, variables) => {
      queryClient.invalidateQueries({ queryKey: ['companies', variables.id] });
    },
  });
}

/**
 * Hook para upgrade de plan
 */
export function useUpgradePlan() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, plan }: { id: string; plan: 'free' | 'starter' | 'professional' | 'enterprise' }) =>
      companiesService.upgradePlan(id, { plan }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['companies', variables.id] });
      queryClient.invalidateQueries({ queryKey: ['companies'] });
    },
  });
}

